import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../lib/api';

/**
 * Database-backed draft autosave for long forms (SO, SE, SR, PE, PO, PR).
 * Auto-saves every `interval` ms to /api/drafts.
 * 
 * Usage:
 *   const draft = useDraft('SO', {
 *     enabled: !isEditMode,
 *     interval: 5000,
 *     initialDraftId: location.state?.draftId,
 *   });
 *   // Call draft.save(formData, title) to trigger save
 *   // Call draft.clear() after successful submit
 *   // draft.hasDraft — true if a draft exists
 *   // draft.draftStatus — '', 'saving', 'saved', 'error'
 */
export function useDraft(type, { enabled = true, interval = 5000, initialDraftId = null } = {}) {
  const [draftId, setDraftId] = useState(initialDraftId);
  const [hasDraft, setHasDraft] = useState(!!initialDraftId);
  const [draftStatus, setDraftStatus] = useState('');
  const pendingDataRef = useRef(null);
  const pendingTitleRef = useRef('');
  const lastSavedRef = useRef('');
  const timerRef = useRef(null);
  const savingRef = useRef(false);
  const draftIdRef = useRef(initialDraftId);

  useEffect(() => { draftIdRef.current = draftId; }, [draftId]);

  const doSave = useCallback(async () => {
    if (!enabled || savingRef.current) return;
    const data = pendingDataRef.current;
    if (!data) return;
    const dataStr = JSON.stringify(data);
    if (dataStr === lastSavedRef.current) return;

    savingRef.current = true;
    setDraftStatus('saving');
    try {
      const res = await api.post('/drafts', {
        id: draftIdRef.current || undefined,
        type: type.toUpperCase(),
        form_data: data,
        title: pendingTitleRef.current || `${type} Draft`,
      });
      if (res.data.id && !draftIdRef.current) {
        setDraftId(res.data.id);
        draftIdRef.current = res.data.id;
      }
      lastSavedRef.current = dataStr;
      setHasDraft(true);
      setDraftStatus('saved');
    } catch {
      setDraftStatus('error');
    } finally {
      savingRef.current = false;
    }
  }, [enabled, type]);

  // Queue data for next auto-save cycle
  const save = useCallback((data, title) => {
    if (!enabled) return;
    pendingDataRef.current = data;
    if (title) pendingTitleRef.current = title;
  }, [enabled]);

  // Load draft data from DB
  const load = useCallback(async (id) => {
    const did = id || draftId;
    if (!did) return null;
    try {
      const res = await api.get(`/drafts/${did}`);
      return res.data?.form_data || null;
    } catch { return null; }
  }, [draftId]);

  // Delete draft from DB
  const clear = useCallback(async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    const did = draftIdRef.current;
    if (did) {
      try { await api.delete(`/drafts/${did}`); } catch { /* ignore */ }
    }
    setDraftId(null);
    draftIdRef.current = null;
    setHasDraft(false);
    setDraftStatus('');
    pendingDataRef.current = null;
    lastSavedRef.current = '';
  }, []);

  // Auto-save timer
  useEffect(() => {
    if (!enabled) return;
    timerRef.current = setInterval(doSave, interval);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [enabled, interval, doSave]);

  return { draftId, hasDraft, setHasDraft, draftStatus, save, load, clear, saveDraft: doSave };
}
